/**
 * Numéro 6 du programme pédagogique ETM — La mécanique.
 *
 * Thème ETM 'M'. Check-list pré-trajet, entretien moto-spécifique,
 * pneus / freins / éclairage / transmission. Très moto-spécifique vs voiture.
 */

import { Wrench, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const mecaniqueEtmIssue = {
  slug: 'mecanique',
  themeCode: 'M',
  issueNumber: 6,
  totalIssues: 9,
  category: 'La mécanique',
  shortTitle: 'La mécanique',
  title: "Une moto bien réglée, c'est ta vie",
  kicker: 'Numéro 6 du programme pédagogique ETM',
  subtitle:
    "Pneus, freins, éclairage, transmission, niveaux — la check-list de pré-trajet qui te ramène entier. Le numéro qui transforme l'angoisse mécanique en confiance.",
  heroImage: '/img/magazines/mecanique.jpg',
  heroImageAlt: 'Moto stationnée avec détails mécaniques apparents — illustration de l’entretien moto',
  readTimeMin: 13,

  seoDescription:
    "Mécanique moto pour ETM 2026 : check-list pré-trajet, pression pneus, plaquettes, éclairage, transmission, niveaux. Lecture pédagogique + QCM final auto-évalué.",
  seoKeywords:
    "mécanique moto, pression pneus moto, freins moto, entretien moto, check-list moto, ETM mécanique",

  edito: {
    title: 'Pourquoi 1 motard sur 4 part avec un défaut critique',
    body: "Selon une étude AFDM 2023, <strong>26 % des motos contrôlées</strong> sur le bord de la route présentent au moins un défaut mécanique majeur : pression de pneus, plaquettes usées, ampoule grillée, chaîne sans tension. Ces défauts ne sont pas une fatalité — ce sont des oublis. Ce numéro de 13 minutes te donne la <strong>check-list pré-trajet en 5 points</strong> que tout motard pro applique en 90 secondes avant chaque départ. À la fin, le QCM te dit si tu pourrais détecter le défaut qui te ferait chuter.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: Wrench,
      partLabel: 'Chapitre 1',
      title: 'Les pneus : le seul lien entre toi et le bitume',
      paragraphs: [
        "Un motard a <strong>deux fois moins de surface de contact</strong> avec la route qu'une voiture par roue (≈ la taille d'une carte de crédit par pneu). Tout passe par là : freinage, virage, accélération. Un pneu mal entretenu = chute garantie.",
        "<strong>Pression</strong> : à contrôler <strong>tous les 15 jours</strong> à froid. Valeurs gravées sur ta carte grise ou indiquées sous la selle. Sous-gonflage = échauffement = éclatement. Surgonflage = adhérence réduite = chute en virage. Une variation de <strong>0,2 bar</strong> peut suffire à modifier sensiblement le comportement.",
        "<strong>Usure</strong> : témoins (TWI) intégrés dans les rainures. Profondeur minimale légale : <strong>1 mm</strong> (article R314-1). Avant cette limite, ton freinage sur sol mouillé est déjà fortement dégradé. Idéalement, change avant <strong>1,5 mm</strong>.",
        "<strong>Date de fabrication</strong> : code DOT à 4 chiffres sur le flanc (semaine + année). Un pneu de plus de <strong>5-6 ans</strong> a perdu ses qualités, même neuf dans l'emballage. Le caoutchouc durcit.",
        "<strong>Pneu avant ≠ pneu arrière</strong> : profil différent (pointe vs carré), pression différente. Inversement = comportement imprévisible. Les pneus doivent toujours être de la <strong>même monte</strong> (marque + modèle) pour un comportement homogène.",
      ],
      keyPoints: [
        'Pression à contrôler tous les 15 jours à froid',
        'Usure minimale légale : 1 mm (TWI)',
        'Durée de vie max : 5-6 ans même neuf',
        'Pneus avant et arrière de la même monte',
      ],
    },

    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "À <strong>50 km/h sur sol mouillé</strong>, la distance de freinage passe de 14 m (pneu neuf) à <strong>27 m</strong> (pneu à 1 mm de profondeur restante). C'est 13 m de plus, soit deux longueurs de voiture. À 90 km/h, l'écart devient catastrophique : 35 m vs 75 m. Tes pneus, c'est ton freinage, c'est ta vie.",
    },

    {
      type: 'lesson',
      icon: Wrench,
      partLabel: 'Chapitre 2',
      title: 'Le freinage : 70 % avant, 30 % arrière',
      paragraphs: [
        "Sur une moto, <strong>environ 70 % de l'efficacité de freinage</strong> provient du frein avant en charge maximale (transfert de masse). Mais utiliser uniquement l'avant = passage par-dessus le guidon. La règle d'or : <strong>les deux freins, toujours, dosés</strong>.",
        "<strong>Plaquettes de frein</strong> : épaisseur minimale 1,5-2 mm (vérifier le manuel). Un grincement métallique au freinage = usure complète = remplacement immédiat. Ignorer le bruit = endommager le disque (300 € de plus).",
        "<strong>Disques</strong> : à inspecter à chaque révision. Tu cherches des stries profondes, une décoloration bleue (surchauffe), ou une épaisseur sous le minimum gravé sur le bord (typiquement 4 mm). Un disque voilé se reconnaît à la pulsation dans le levier au freinage.",
        "<strong>Liquide de frein</strong> : à changer tous les <strong>2 ans</strong>. Le liquide DOT 4 absorbe l'humidité de l'air ambiant — au-delà de 2 ans, ce sont des risques de vapor-lock (formation de bulles) lors d'un freinage soutenu. Symptôme : le levier devient mou ou tombe en butée.",
        "<strong>ABS</strong> : obligatoire sur toutes les motos > 125 cm³ vendues depuis 2017. Permet un freinage d'urgence sans blocage. Ne le désactive jamais en usage routier (certaines motos trail le permettent pour le hors-piste uniquement).",
      ],
      keyPoints: [
        'Toujours les 2 freins, dosés (70/30 avant/arrière)',
        'Plaquettes : remplacer sous 1,5-2 mm',
        'Liquide de frein : changer tous les 2 ans',
        'ABS obligatoire > 125 cm³ depuis 2017',
      ],
    },

    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Ma chaîne a 20 000 km, elle tient encore. » <strong>FAUX.</strong> Une chaîne usée s'étire (élongation > 1,5 %), saute des dents sur le pignon arrière sous accélération brutale, et peut <strong>se rompre en pleine action</strong>. Conséquence : projection violente vers le bas, blocage du bras oscillant, chute immédiate. Une chaîne se contrôle <strong>tous les 1 000 km</strong> : tension (jeu vertical 25-35 mm au milieu du brin inférieur), lubrification, élongation. À remplacer en kit (chaîne + pignons) tous les 25 000-40 000 km selon usage.",
    },

    {
      type: 'lesson',
      icon: Wrench,
      partLabel: 'Chapitre 3',
      title: "L'éclairage : être vu, c'est exister",
      paragraphs: [
        "À moto, <strong>87 % des accidents en intersection</strong> impliquent un automobiliste qui dit \"je ne l'avais pas vu\". L'éclairage est ton seul mégaphone pour signaler ta présence — y compris en plein jour.",
        "<strong>Feu de croisement (code) allumé en permanence</strong> : obligatoire jour et nuit depuis 1975 sur toute moto (article R313-32). Ce n'est pas une option : c'est ton premier équipement de sécurité. Un feu cassé = -50 % de visibilité en intersection.",
        "<strong>Clignotants</strong> : 2 avant, 2 arrière, intermittence 60 à 120 cycles/minute. Visible à <strong>50 m</strong> minimum. Tester en pré-trajet : un clignotant grillé = défaut majeur = 35 € + 1 point retiré au contrôle.",
        "<strong>Feu stop</strong> : s'allume à l'action du frein avant ET du frein arrière. Vérifie les 2 au pré-trajet (un ami derrière, ou le mur d'un parking). C'est LE signal qui évite la collision arrière sur autoroute.",
        "<strong>Plaque d'immatriculation éclairée la nuit</strong> : oui c'est obligatoire (souvent oublié). Une ampoule de plaque cassée = 35 € au contrôle. Vérifier 2 fois/an.",
      ],
      keyPoints: [
        'Code allumé en permanence (obligatoire depuis 1975)',
        'Clignotants visibles à 50 m minimum',
        'Tester les 2 freins (avant + arrière) déclenchent le stop',
        'Plaque éclairée la nuit obligatoire',
      ],
    },

    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Ma check-list pré-trajet, je la fais en <strong>90 secondes</strong> chaque matin avec mes élèves. Mnémonique : <strong>FREP</strong> — <strong>F</strong>reins (avant + arrière, pas de mou au levier), <strong>R</strong>oues (pression, usure visible, corps étranger éventuel), <strong>É</strong>clairage (code + stop + clignotants), <strong>P</strong>neumatique (niveau d'huile sur le hublot moteur). Si un point cloche, tu ne pars pas. C'est exactement ce que fait un pilote d'avion avant chaque vol. Et toi, c'est ta vie. »",
    },

    {
      type: 'story',
      icon: Users,
      title: 'Le témoignage : « Mon clignotant a déclenché l\'accident »',
      body: "« Un soir d'hiver, je quitte le parking, je m'engage sur le boulevard. Je tourne à gauche. Un automobiliste m'emboutit à l'avant. Bilan : moto détruite, cheville cassée, 6 semaines d'arrêt. <strong>Mon clignotant gauche était grillé depuis 3 semaines</strong>, je ne l'avais jamais vérifié. L'automobiliste n'a rien vu venir. Devant l'assurance : <strong>50 % de responsabilité partagée</strong> car défaut technique de ma moto. Aujourd'hui, je teste TOUS mes feux avant de partir. Ça prend 10 secondes. » <em>— Karim, 26 ans</em>",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '26 %',
    middle: 'des motos contrôlées',
    label: "présentent au moins un défaut mécanique majeur lors d'un contrôle routier",
    source: 'Étude AFDM 2023',
    accent: '#1d4ed8',
  },

  glossary: [
    { term: 'TWI', def: 'Tread Wear Indicator. Petit bossage dans les rainures du pneu qui signale la limite légale d\'usure (1 mm de profondeur restante).' },
    { term: 'DOT', def: 'Department of Transportation. Code à 4 chiffres sur le flanc du pneu indiquant la semaine et l\'année de fabrication (ex : 2624 = 26e semaine 2024).' },
    { term: 'ABS', def: 'Anti-lock Braking System. Système empêchant le blocage des roues lors d\'un freinage d\'urgence. Obligatoire sur toutes motos > 125 cm³ depuis 2017.' },
    { term: 'DOT 4', def: 'Norme du liquide de frein moto. À changer tous les 2 ans car il absorbe l\'humidité de l\'air ambiant (hygroscopique).' },
    { term: 'Vapor-lock', def: 'Phénomène d\'évaporation du liquide de frein vieilli sous forte chaleur (freinage soutenu en descente). Symptôme : levier mou ou en butée.' },
    { term: 'Plaquettes de frein', def: 'Éléments en composite friction qui appuient sur le disque pour freiner. À remplacer sous 1,5-2 mm d\'épaisseur restante (vérifier manuel).' },
    { term: 'Transfert de masse', def: 'En freinage moto, environ 70 % du poids bascule sur la roue avant, expliquant la prépondérance du frein avant dans l\'efficacité totale.' },
    { term: 'Disque voilé', def: 'Disque de frein légèrement déformé (impact, surchauffe). Se reconnaît à une pulsation rythmique au levier de frein. À remplacer.' },
    { term: 'Élongation chaîne', def: 'Allongement progressif d\'une chaîne usée. Au-delà de 1,5 %, risque de saut de dent sur pignon = rupture potentielle.' },
    { term: 'FREP', def: 'Mnémonique check-list pré-trajet : Freins, Roues, Éclairage, Pneumatique (niveau d\'huile). 90 secondes par jour.' },
  ],

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur la mécanique moto tirées de la banque officielle ETM. Tu dois en réussir <strong>au moins 8 sur 10</strong> pour valider ta maîtrise du thème. Un thème très moto-spécifique : prends ton temps.",
    themeCode: 'M',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: {
    slug: 'route',
    number: 1,
    title: 'Lire la route comme un motard',
    short: 'La route',
  },
};

export default mecaniqueEtmIssue;
